import { useCallback, useEffect, useRef, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";
import { channelFromApi, type ChannelConnection, type ChannelStatus } from "../types/channel";

export type AdminChannelsStatus = "loading" | "ready" | "error";

export type AdminChannelResult =
  | { ok: true }
  | { ok: false; message: string };

export type UseAdminChannelsResult = {
  status: AdminChannelsStatus;
  channels: ReadonlyArray<ChannelConnection>;
  error: string | null;
  setStatus: (channel: string, status: ChannelStatus, accountLabel?: string | null) => Promise<AdminChannelResult>;
  remove: (channel: string) => Promise<AdminChannelResult>;
};

type ServerError = { code: string; message: string };

const base = (tenantId: string): string => `/api/admin/channels/${encodeURIComponent(tenantId)}`;

export const useAdminChannels = (tenantId: string | null): UseAdminChannelsResult => {
  const [status, setStatusState] = useState<AdminChannelsStatus>("loading");
  const [channels, setChannels] = useState<ReadonlyArray<ChannelConnection>>([]);
  const [error, setError] = useState<string | null>(null);
  // Per-channel request sequence so out-of-order responses can't clobber newer
  // state when an operator flips a dropdown twice quickly.
  const seqRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (tenantId === null) {
      setChannels([]);
      setStatusState("ready");
      setError(null);
      return;
    }
    let cancelled = false;
    setStatusState("loading");
    setError(null);

    void fetch(base(tenantId), { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error(`admin channels fetch failed (${res.status})`);
        const raw = (await res.json()) as ReadonlyArray<Parameters<typeof channelFromApi>[0]>;
        if (cancelled) return;
        setChannels(raw.map(channelFromApi));
        setStatusState("ready");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setStatusState("error");
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const setStatus = useCallback(
    async (
      channel: string,
      next: ChannelStatus,
      accountLabel?: string | null,
    ): Promise<AdminChannelResult> => {
      if (tenantId === null) return { ok: false, message: "no tenant selected" };
      const mySeq = (seqRef.current.get(channel) ?? 0) + 1;
      seqRef.current.set(channel, mySeq);
      try {
        const res = await fetch(`${base(tenantId)}/${encodeURIComponent(channel)}`, {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ status: next, accountLabel: accountLabel ?? null }),
        });
        if (res.ok) {
          const updated = channelFromApi((await res.json()) as Parameters<typeof channelFromApi>[0]);
          if (seqRef.current.get(channel) === mySeq) {
            setChannels((prev) => {
              const idx = prev.findIndex((c) => c.channel === channel);
              if (idx === -1) return [...prev, updated];
              const copy = prev.slice();
              copy[idx] = updated;
              return copy;
            });
          }
          return { ok: true };
        }
        const body = (await res.json().catch(() => null)) as ServerError | null;
        return { ok: false, message: body?.message ?? `update failed (${res.status})` };
      } catch (err: unknown) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [tenantId],
  );

  const remove = useCallback(
    async (channel: string): Promise<AdminChannelResult> => {
      if (tenantId === null) return { ok: false, message: "no tenant selected" };
      seqRef.current.set(channel, (seqRef.current.get(channel) ?? 0) + 1);
      try {
        const res = await fetch(`${base(tenantId)}/${encodeURIComponent(channel)}`, {
          method: "DELETE",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        });
        if (res.ok) {
          setChannels((prev) => prev.filter((c) => c.channel !== channel));
          return { ok: true };
        }
        const body = (await res.json().catch(() => null)) as ServerError | null;
        return { ok: false, message: body?.message ?? `remove failed (${res.status})` };
      } catch (err: unknown) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [tenantId],
  );

  return { status, channels, error, setStatus, remove };
};
